import { Flex, FormControl, FormErrorMessage, Box } from "@chakra-ui/react";
import { useDefault } from "@uidotdev/usehooks";
import { FormProvider, useForm } from "react-hook-form";
import Input from "../inputs/Input";
import Select from "../inputs/Select";
import FileInput from "../FileInput";
import { forwardRef, useImperativeHandle } from "react";
import { Documentation } from "../../types/documentation.types";

type DocumentationFormProps = {
	value?: Partial<Documentation>;
};

export type DocumentationFormHandler = {
	valid: () => boolean;
	values: () => Documentation;
};

const DocumentationForm = forwardRef<
	DocumentationFormHandler,
	DocumentationFormProps
>((_, ref) => {
	const methods = useForm();
	const active = { bgColor: "brand.700", color: "white", borderRadius: 4 };
	const [type, setType] = useDefault("link", "link");

	const handleSetTab = (tab: string) => {
		setType(tab);
		methods.clearErrors();
	};
	useImperativeHandle(ref, () => ({
		valid: () => {
			let valid = true;
			methods.clearErrors();
			if (type === "link") {
				const values = methods.getValues();
				const mandatoryFields = ["title", "link", "date", "category"];
				mandatoryFields.forEach((field) => {
					if (typeof values[field] !== "string" || values[field].length === 0) {
						methods.setError(field, {
							type: "required",
							message: "Ce champ est obligatoire",
						});
						valid = false;
					}
				});
			}
			if (type === "file") {
				const values = methods.getValues();
				const mandatoryFields = ["title", "file", "date", "category"];
				mandatoryFields.forEach((field) => {
					if (values[field].length === 0) {
						methods.setError(field, {
							type: "required",
							message: "Ce champ est obligatoire",
						});
						valid = false;
					}
				});
			}
			return valid;
		},
		values: () => {
			const values = methods.getValues();
			return {
				type,
				url: type === "link" ? values["link"] : undefined,
				file: type === "file" ? values["file"][0] : undefined,
				title: values["title"],
				date: values["date"],
				category: values["category"],
			};
		},
	}));
	return (
		<FormProvider {...methods}>
			<form style={{ marginTop: "32px" }}>
				<Flex direction="column" gap="24px">
					<Flex w="100%" bgColor="#EEF5FF" p={1} borderRadius={4}>
						<Box
							w="100%"
							textAlign="center"
							py={1}
							{...(type === "link" ? active : {})}
							onClick={() => handleSetTab("link")}
						>
							Lien
						</Box>
						<Box
							w="100%"
							textAlign="center"
							py={1}
							{...(type === "file" ? active : {})}
							onClick={() => handleSetTab("file")}
						>
							Fichier
						</Box>
					</Flex>

					<FormControl isInvalid={!!methods.formState.errors.title}>
						<Input
							id="title"
							placeholder="Titre"
							{...methods.register("title", {
								required: "Ce champ est obligatoire",
							})}
						></Input>
						<FormErrorMessage>
							<>{methods.formState.errors?.title?.message}</>
						</FormErrorMessage>
					</FormControl>

					<FormControl
						isInvalid={!!methods.formState.errors.link}
						display={type === "link" ? "block" : "none"}
					>
						<Input
							id="link"
							placeholder="Lien"
							{...methods.register("link")}
						></Input>
						<FormErrorMessage>
							<>{methods.formState.errors?.link?.message}</>
						</FormErrorMessage>
					</FormControl>

					<Flex gap={4}>
						<FormControl isInvalid={!!methods.formState.errors.date}>
							<Input
								id="date"
								placeholder="Date"
								type="date"
								{...methods.register("date", {
									required: "Ce champ est obligatoire",
								})}
							></Input>
							<FormErrorMessage>
								<>{methods.formState.errors?.date?.message}</>
							</FormErrorMessage>
						</FormControl>

						<FormControl isInvalid={!!methods.formState.errors.category}>
							<Select
								id="category"
								defaultValue={"documentation"}
								placeholder="Catégorie"
								{...methods.register("category", {
									required: "Ce champ est obligatoire",
								})}
								noEmpty
							>
								<option value="documentation">Documentation</option>
							</Select>
							<FormErrorMessage>
								<>{methods.formState.errors?.category?.message}</>
							</FormErrorMessage>
						</FormControl>
					</Flex>

					<FormControl isInvalid={!!methods.formState.errors.file}>
						<FileInput
							display={type === "file" ? "flex" : "none"}
							{...methods.register("file", {
								required: "Ce champ est obligatoire",
							})}
						></FileInput>
						<FormErrorMessage>
							<>{methods.formState.errors?.file?.message}</>
						</FormErrorMessage>
					</FormControl>
				</Flex>
			</form>
		</FormProvider>
	);
});

export default DocumentationForm;
